import axios from "axios";
import { API_CONFIG, API_ENDPOINTS } from "../config/api.config";

/**
 * Token 服務 - 業界最佳實踐
 * 專門處理認證相關的 token 操作
 */

/**
 * 刷新訪問令牌
 * @param {string} refreshToken - 刷新令牌
 * @returns {Promise<string|null>} 新的訪問令牌或 null
 */
export const refreshAccessToken = async (refreshToken) => {
  if (!refreshToken) {
    console.warn("🔄 No refresh token provided");
    return null;
  }

  try {
    // 使用獨立的 axios 實例，避免攔截器循環
    const response = await axios.post(
      `${API_CONFIG.FULL_URL}${API_ENDPOINTS.AUTH.REFRESH}`,
      { refresh: refreshToken },
      {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: API_CONFIG.TIMEOUT,
      }
    );

    const { access } = response.data;
    
    if (access) {
      localStorage.setItem("access_token", access);
      console.log("🔄 Token refreshed successfully");
      return access;
    }
    
    throw new Error("No access token in response");
  } catch (error) {
    console.error("🔄 Token refresh failed:", error.response?.data || error.message);
    
    // 清除無效的 token
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    
    return null;
  }
};

/**
 * 檢查 token 是否過期
 * @param {string} token - JWT token
 * @returns {boolean} true 如果過期
 */
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    
    // 提前 5 分鐘判斷為過期，預留刷新時間
    return payload.exp < (currentTime + 300);
  } catch (error) {
    console.error("🔍 Token parsing failed:", error);
    return true;
  }
};

/**
 * 獲取有效的訪問令牌
 * 如果當前 token 即將過期，自動刷新
 * @returns {Promise<string|null>} 有效的訪問令牌
 */
export const getValidAccessToken = async () => {
  const accessToken = localStorage.getItem("access_token");
  const refreshToken = localStorage.getItem("refresh_token");
  
  // 沒有任何 token
  if (!accessToken && !refreshToken) {
    return null;
  }
  
  // 訪問令牌仍然有效
  if (accessToken && !isTokenExpired(accessToken)) {
    return accessToken;
  }
  
  // 嘗試刷新訪問令牌
  if (refreshToken) {
    const newToken = await refreshAccessToken(refreshToken);
    return newToken;
  }
  
  return null;
};

/**
 * 清除所有認證信息
 */
export const clearAuthTokens = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user");
  console.log("🧹 Auth tokens cleared");
};

/**
 * 設置認證令牌
 * @param {string} accessToken - 訪問令牌
 * @param {string} refreshToken - 刷新令牌
 */
export const setAuthTokens = (accessToken, refreshToken) => {
  if (accessToken) {
    localStorage.setItem("access_token", accessToken);
  }
  if (refreshToken) {
    localStorage.setItem("refresh_token", refreshToken);
  }
  console.log("💾 Auth tokens saved");
};

// 向後兼容的函數
export const refreshToken = refreshAccessToken;